import Button from '@brands/Components/Button/Button';
import Container from '@brands/Components/Container/Container';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  type AppointmentDetails,
  declineAppointment,
  errorMessages,
  fetchAppointmentDetails,
  getErrorCode,
  getErrorMessage,
  getSuccessMessage,
  isSuccessCase,
} from '../services/providerConfirmation';
import styles from './styles.module.scss';

const ProviderDeclinePage: React.FC = () => {
  const { confirmationToken } = useParams<{ confirmationToken: string }>();
  const navigate = useNavigate();

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appointmentDetails, setAppointmentDetails] = useState<AppointmentDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);

  // Auto-decline appointment on component mount
  useEffect(() => {
    const autoDeclineAppointment = async (): Promise<void> => {
      if (!confirmationToken) {
        setError(errorMessages.INVALID_TOKEN);
        setIsLoadingDetails(false);
        return;
      }

      try {
        setIsLoadingDetails(true);

        // First fetch appointment details
        const data = await fetchAppointmentDetails(confirmationToken);
        setAppointmentDetails(data);

        // Then automatically decline the appointment
        await declineAppointment(confirmationToken);
        setSuccess(true);
        toast.success('Appointment declined successfully.');
      } catch (err) {
        console.error('Error auto-declining appointment:', err);

        if (isSuccessCase(err)) {
          setSuccess(true);
          const successMessage = getSuccessMessage(err) || 'Appointment already declined';
          toast.success(successMessage);
        } else {
          const errorCode = getErrorCode(err);

          if (errorCode === 'ERR_PROVIDER_CONFIRMATION_NOT_FOUND' || errorCode === '[11703-0]') {
            setError(errorMessages.INVALID_TOKEN);
          } else if (errorCode === 'ERR_PROVIDER_CONFIRMATION_CANNOT_DECLINE_CONFIRMED' || errorCode === '[11703-3]') {
            setError(errorMessages.CANNOT_DECLINE_CONFIRMED);
          } else {
            setError(getErrorMessage(err));
          }
        }
      } finally {
        setIsLoadingDetails(false);
      }
    };

    autoDeclineAppointment();
  }, [confirmationToken]);

  const handleBackToLogin = (): void => {
    navigate('/');
  };

  const handleRequestNewLink = (): void => {
    window.open('mailto:support@dentistry.one?subject=Request New Confirmation Link', '_blank');
  };

  const handleContactSupport = (): void => {
    // TODO: Implement contact support functionality
    window.open('mailto:support@dentistry.one?subject=Appointment Support Request', '_blank');
  };

  if (isLoadingDetails) {
    return (
      <Container>
        <div className={styles.loadingContainer}>
          <Loading dotted />
          <p className={styles.loadingText}>Declining appointment...</p>
        </div>
      </Container>
    );
  }

  if (error && !appointmentDetails) {
    const isInvalidToken = error === errorMessages.INVALID_TOKEN;

    return (
      <Container>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>⚠️</div>
          <h2 className={styles.errorTitle}>Unable to Load Appointment</h2>
          <p className={styles.errorMessage}>{error}</p>

          {isInvalidToken ? (
            <div className={styles.errorActions}>
              <Button type="button" className={styles.requestLinkButton} onClick={handleRequestNewLink}>
                Request New Link
              </Button>
              <Button type="button" className={styles.backButton} onClick={handleBackToLogin}>
                Back to Login
              </Button>
            </div>
          ) : (
            <Button type="button" className={styles.backButton} onClick={handleBackToLogin}>
              Back to Login
            </Button>
          )}
        </div>
      </Container>
    );
  }

  if (success) {
    return (
      <Container>
        <div className={styles.successContainer}>
          <div className={styles.successIcon}>ℹ️</div>
          <h2 className={styles.successTitle}>Appointment Declined</h2>
          <p className={styles.successMessage}>
            The system will attempt to find another provider or notify the patient to reschedule.
          </p>
          {appointmentDetails && (
            <div className={styles.appointmentSummary}>
              <h3>Declined Appointment Details</h3>
              <div className={styles.detailRow}>
                <span className={styles.label}>Patient:</span>
                <span className={styles.value}>{appointmentDetails.patientName}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Date & Time:</span>
                <span className={styles.value}>
                  {appointmentDetails.scheduledDate} at {appointmentDetails.scheduledTime}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>Type:</span>
                <span className={styles.value}>{appointmentDetails.appointmentType}</span>
              </div>
            </div>
          )}
          <div className={styles.successActions}>
            <Button type="button" className={styles.supportButton} onClick={handleContactSupport}>
              Contact Support
            </Button>
            <Button type="button" className={styles.backButton} onClick={handleBackToLogin}>
              Back to Login
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className={styles.declineContainer}>
        <div className={styles.header}>
          <h1 className={styles.title}>Decline Appointment</h1>
          <p className={styles.subtitle}>Please review the appointment details below before confirming your decline.</p>
        </div>

        {appointmentDetails && (
          <div className={styles.appointmentDetails}>
            <h2 className={styles.detailsTitle}>Appointment Information</h2>

            <div className={styles.detailCard}>
              <div className={styles.detailRow}>
                <span className={styles.label}>Patient Name:</span>
                <span className={styles.value}>{appointmentDetails.patientName}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Provider:</span>
                <span className={styles.value}>{appointmentDetails.providerName}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Appointment Type:</span>
                <span className={styles.value}>{appointmentDetails.appointmentType}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Scheduled Date:</span>
                <span className={styles.value}>{appointmentDetails.scheduledDate}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Scheduled Time:</span>
                <span className={styles.value}>{appointmentDetails.scheduledTime}</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className={styles.errorAlert}>
            <span className={styles.errorIcon}>⚠️</span>
            <span className={styles.errorText}>{error}</span>
          </div>
        )}

        <div className={styles.footer}>
          <p className={styles.footerText}>
            If you have any questions about this appointment, please contact our support team.
          </p>
        </div>
      </div>
    </Container>
  );
};

export default ProviderDeclinePage;
